// Rastreo de los envios por paqueteria (Skydropx) · como se le dice a la vendedora cada estado.
//
// 4-nov-2026 (dueño): "que en la PWA se reflejen las entregas cuando las reporte Skydropx y, mientras
// va en camino, que diga En Tránsito". Se muestran TODOS los estados; los que piden hacer algo
// (intento fallido, en sucursal, devolución, problema...) van en naranja para que se vean primero.
//
// El servidor guarda en envio.rastreo lo que contestó Skydropx (src/services/rastreoEnvios.js):
//   { estado, fecha, entregado_at, lugar, devolucion, sin_eventos, consultado_at, error, ... }
// En la lista llega resumido (error es true/false); al abrir el pedido llega completo, con eventos.
// Aquí solo hay funciones puras: sin React, para poder probarlas solas.

// Ya no cambian: el servidor deja de preguntar y la pantalla no se actualiza sola.
export const ESTADOS_FINALES = ['delivered', 'canceled', 'destroyed'];

// Los que piden que alguien haga algo: van en naranja y arriba en la lista.
const ALERTA = {
  delivery_attempt: 'Intento de entrega fallido',
  delivered_to_branch: 'En sucursal · la clienta debe recogerlo',
  exception: 'Problema con la paquetería',
  in_return: 'Regresando a la tienda',
  canceled: 'Guía cancelada',
  destroyed: 'Paquete destruido',
  retained: 'Retenido por la paquetería',
};

// Cada movimiento de la lista del pedido. Aquí sí se distingue "lo recogieron" de "va en camino":
// en la tarjeta las dos cosas son "En tránsito", pero en el detalle sirve saber cuándo salió.
const EVENTO = {
  created: 'Guía hecha',
  picked_up: 'Lo recogió la paquetería',
  in_transit: 'En tránsito',
  last_mile: 'En reparto',
  delivered: 'Entregado',
  ...ALERTA,
  delivered_to_branch: 'Llegó a la sucursal',
};

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MX = 6 * 3600000;   // Mexico: UTC-6 fijo, sin horario de verano, igual que el resto de la PWA

// La fecha y hora de Mexico de un ISO de Skydropx (viene en UTC con Z), o null si no sirve.
function enMexico(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t - MX);
}

// "28 oct"
export function fechaCorta(iso) {
  const d = enMexico(iso);
  return d ? d.getUTCDate() + ' ' + MESES[d.getUTCMonth()] : '';
}

// "28 oct 13:25"
export function fechaHora(iso) {
  const d = enMexico(iso);
  return d ? fechaCorta(iso) + ' ' + horaMx(iso) : '';
}

// "13:25"
export function horaMx(iso) {
  const d = enMexico(iso);
  return d ? d.getUTCHours() + ':' + String(d.getUTCMinutes()).padStart(2, '0') : '';
}

// "hace 5 min", para el "Revisado hace..." del detalle.
export function haceCuanto(iso, ahoraMs) {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, ((ahoraMs == null ? Date.now() : ahoraMs) - t) / 1000);
  if (s < 60) return 'hace un momento';
  if (s < 3600) return 'hace ' + Math.floor(s / 60) + ' min';
  if (s < 86400) return 'hace ' + Math.floor(s / 3600) + ' h';
  const d = Math.floor(s / 86400);
  return 'hace ' + d + (d === 1 ? ' día' : ' días');
}

export const esFinal = r => !!(r && ESTADOS_FINALES.includes(r.estado));

// Entregado de verdad: si en el camino hubo devolución, lo "entregado" es el regreso a la tienda.
export const esEntregado = r => !!(r && r.estado === 'delivered' && !r.devolucion);

// Lo que dice el chip y de qué color va. tono: 'neutral' | 'camino' (azul) | 'ok' (verde) | 'alerta' (naranja).
export function etiqueta(r) {
  if (!r) return { texto: 'Rastreo pendiente', tono: 'neutral' };
  const e = r.estado;
  if (e === 'delivered') {
    if (r.devolucion) return { texto: 'Regresó a la tienda', tono: 'alerta' };
    const f = fechaCorta(r.entregado_at || r.fecha);
    return { texto: 'Entregado' + (f ? ' ' + f : ''), tono: 'ok' };
  }
  // Ya hubo devolución (in_return): lo que sigue (en tránsito, en reparto...) es el camino de REGRESO,
  // no "va en camino a la clienta". Sigue en naranja.
  if (r.devolucion && !ALERTA[e]) return { texto: 'Regresando a la tienda', tono: 'alerta' };
  if (ALERTA[e]) return { texto: ALERTA[e], tono: 'alerta' };
  if (e === 'picked_up' || e === 'in_transit') return { texto: 'En tránsito', tono: 'camino' };
  if (e === 'last_mile') return { texto: 'En reparto', tono: 'camino' };
  if (e === 'created') return { texto: 'Guía hecha · aún no la recogen', tono: 'neutral' };
  // Un estado que Skydropx agregue despues: se dice tal cual en vez de adivinar que va en camino.
  if (e) return { texto: 'Rastreo: ' + e, tono: 'neutral' };
  if (r.sin_eventos) return { texto: 'Sin movimientos todavía', tono: 'neutral' };
  if (r.error) return { texto: 'No se pudo revisar el rastreo', tono: 'neutral' };
  return { texto: 'Rastreo pendiente', tono: 'neutral' };
}

export const esAlerta = r => etiqueta(r).tono === 'alerta';

// En qué sección de la lista va un envío que ya tiene guía.
export function grupo(r) {
  if (esAlerta(r)) return 'atencion';
  if (esEntregado(r)) return 'entregado';
  return 'transito';
}

// El tono a la clase de los chips de la lista (.el-chip): naranja es la de "espera" que ya existía.
export const claseChip = tono => (tono === 'alerta' ? ' espera' : tono === 'ok' ? ' ok' : tono === 'camino' ? ' camino' : '');

// El nombre en español de un movimiento de la paquetería.
export function etiquetaEvento(codigo) {
  return EVENTO[codigo] || (codigo ? 'Rastreo: ' + codigo : 'Movimiento');
}

export const tonoEvento = codigo => (ALERTA[codigo] ? 'alerta' : codigo === 'delivered' ? 'ok' : (codigo === 'picked_up' || codigo === 'in_transit' || codigo === 'last_mile') ? 'camino' : 'neutral');
